import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import ExerciseTimer from "./ExerciseTimer";
import { ExerciseSet } from "@/lib/types";

interface ProgressiveTrainingProps {
  sets: number;
  reps?: number;
  duration?: number;
  onSetCompleted: (setIndex: number, completed: boolean) => void;
  exerciseSets: ExerciseSet[];
  disabled?: boolean;
}

// Progressive training technique based on the article
// https://www.focus.de/gesundheit/push-up-training-in-nur-vier-wochen-100-liegestuetze-so-einfach-geht-das_id_13333892.html
const ProgressiveTraining: React.FC<ProgressiveTrainingProps> = ({
  sets,
  reps,
  duration,
  onSetCompleted,
  exerciseSets,
  disabled = false,
}) => {
  const [currentSet, setCurrentSet] = useState(0);
  // Determine initial mode based on exercise sets state
  const getInitialMode = (): "exercise" | "rest" | "complete" | "manual" => {
    if (!exerciseSets || exerciseSets.length === 0) {
      return "manual";
    }

    // If all sets are completed, show complete mode
    if (exerciseSets.every((set) => set.completed)) {
      return "complete";
    }

    // Otherwise default to manual mode
    return "manual";
  };

  const [mode, setMode] = useState<"exercise" | "rest" | "complete" | "manual">(
    getInitialMode()
  ); // Get progress level (1-4) based on current workout difficulty rather than just completion
  // This follows the progressive approach from the article and should be based on prescribed difficulty
  const calculateProgressLevel = (): number => {
    // Instead of basing the level on current completion (which is flawed),
    // base it on the prescribed difficulty for this workout

    // If we have historical high-performance targets (more sets/reps than base),
    // it indicates the user has progressed and deserves a higher difficulty level

    // Base difficulty levels based on prescribed workout intensity
    const baseSets = 3; // Standard number of sets
    const baseReps = reps || 10; // Standard number of reps (if available)
    const baseDuration = duration || 30; // Standard duration (if available)

    // Calculate intensity score based on prescribed workout
    let intensityScore = 0;

    if (reps) {
      // For rep-based exercises, higher reps or more sets = higher difficulty
      const repsIntensity = (reps - 8) / 4; // Scale: 8 reps = 0, 12 reps = 1, 16 reps = 2
      const setsIntensity = (sets - 2) / 2; // Scale: 2 sets = 0, 3 sets = 0.5, 4 sets = 1
      intensityScore = Math.max(0, (repsIntensity + setsIntensity) / 2);
    } else if (duration) {
      // For duration-based exercises, longer duration or more sets = higher difficulty
      const durationIntensity = (duration - 20) / 20; // Scale: 20s = 0, 40s = 1, 60s = 2
      const setsIntensity = (sets - 2) / 2; // Scale: 2 sets = 0, 3 sets = 0.5, 4 sets = 1
      intensityScore = Math.max(0, (durationIntensity + setsIntensity) / 2);
    } else {
      // Default based on number of sets only
      intensityScore = Math.max(0, (sets - 2) / 3); // 2 sets = 0, 3 sets = 0.33, 5 sets = 1
    }

    // Map intensity score to progress levels
    if (intensityScore >= 1.5) return 4; // Expert: Very high intensity
    if (intensityScore >= 1.0) return 3; // Advanced: High intensity
    if (intensityScore >= 0.5) return 2; // Intermediate: Medium intensity
    return 1; // Beginner: Low intensity
  };

  // Fixed progress level based on workout prescription
  const [progressLevel, setProgressLevel] = useState(calculateProgressLevel);

  // Recalculate progress level when exercise parameters change (not just completion)
  useEffect(() => {
    setProgressLevel(calculateProgressLevel());
  }, [sets, reps, duration]); // Only recalculate when the prescribed workout changes

  // Progressive rest periods (factor * exercise duration)
  // Level 1: 1.0x rest time (easiest)
  // Level 2: 0.8x rest time
  // Level 3: 0.6x rest time
  // Level 4: 0.4x rest time (hardest)
  const restFactors = [1.0, 0.8, 0.6, 0.4];

  // Get the appropriate rest factor based on progress level (1-indexed)
  const getRestFactor = () => {
    return restFactors[progressLevel - 1] || 0.6; // Default to 0.6 if invalid level
  };

  // Calculate rest time based on exercise duration and current progress level
  const getRestDuration = () => {
    if (duration) {
      return Math.max(10, Math.round(duration * getRestFactor())); // Minimum 10 seconds rest
    } else if (reps) {
      // For rep-based exercises, estimate 2 seconds per rep as exercise time
      const estimatedExerciseTime = reps * 2;
      return Math.max(10, Math.round(estimatedExerciseTime * getRestFactor()));
    }
    return 30; // Default rest time
  };

  const startExercise = () => {
    setMode("exercise");
  };

  const handleExerciseComplete = () => {
    if (currentSet < sets - 1) {
      setMode("rest");
    } else {
      setMode("complete");
      // Mark all sets as completed
      for (let i = 0; i < sets; i++) {
        onSetCompleted(i, true);
      }
    }
  };

  const handleRestComplete = () => {
    setCurrentSet((prev) => prev + 1);
    setMode("exercise");
  };

  const handleManualSetComplete = (setIndex: number) => {
    onSetCompleted(setIndex, true);

    // If all sets are completed, mark as complete
    if (exerciseSets.every((set) => set.completed)) {
      setMode("complete");
    }
  };

  const handleRestart = () => {
    setCurrentSet(0);
    setMode("manual");
  };

  // For rep-based exercises with manual tracking
  const renderManualMode = () => (
    <div className="mt-4">
      <div className="text-center mb-4">
        <h3 className="font-medium text-sm mb-2">Track sets manually</h3>
        <div className="flex justify-center gap-2 mb-2">
          <Button
            variant="default"
            className="w-full"
            onClick={startExercise}
            disabled={sets === 0 || disabled}
          >
            Start Timer Mode
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        {Array.from({ length: sets }).map((_, idx) => (
          <Button
            key={idx}
            variant={exerciseSets[idx]?.completed ? "default" : "outline"}
            className={`${exerciseSets[idx]?.completed ? "bg-green-600" : ""}`}
            onClick={() => handleManualSetComplete(idx)}
            disabled={disabled}
          >
            Set {idx + 1}
            {exerciseSets[idx]?.completed && (
              <span className="material-icons text-sm ml-1">check</span>
            )}
          </Button>
        ))}
      </div>
    </div>
  );

  // Get label for the current training level
  const getProgressLevelLabel = (level: number): string => {
    switch (level) {
      case 1:
        return "Beginner";
      case 2:
        return "Intermediate";
      case 3:
        return "Advanced";
      case 4:
        return "Expert";
      default:
        return "Beginner";
    }
  };

  // Get description for the current training level
  const getProgressLevelDescription = (level: number): string => {
    switch (level) {
      case 1:
        return "100% rest time - Base level training";
      case 2:
        return "80% rest time - Reduced rest periods";
      case 3:
        return "60% rest time - Short recovery";
      case 4:
        return "40% rest time - Minimum rest";
      default:
        return "Standard rest periods";
    }
  };

  // Progressive training difficulty indicator (no longer selectable)
  const renderProgressiveLevelIndicator = () => (
    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
      <h3 className="font-medium text-sm mb-2">
        Progressive Training Level:{" "}
        <span className="text-blue-600">
          {getProgressLevelLabel(progressLevel)}
        </span>
      </h3>
      <p className="text-xs text-gray-500 mb-3">
        Your training difficulty automatically adapts based on your prescribed
        workout intensity. Current level:{" "}
        {getProgressLevelDescription(progressLevel)}
      </p>

      <div className="grid grid-cols-4 gap-1">
        {[1, 2, 3, 4].map((level) => (
          <div
            key={level}
            className={`p-2 rounded text-center ${
              progressLevel === level
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            {level}
          </div>
        ))}
      </div>
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>Beginner</span>
        <span>Expert</span>
      </div>
    </div>
  );

  return (
    <div className="mt-3">
      <Separator className="my-2" />

      {mode === "manual" && renderManualMode()}

      {mode === "exercise" && (
        <div className="flex flex-col items-center">
          <h3 className="font-medium text-lg mb-1">
            Set {currentSet + 1} of {sets}
          </h3>
          {duration ? (
            <ExerciseTimer
              duration={duration}
              onComplete={handleExerciseComplete}
            />
          ) : (
            <div className="text-center my-4">
              <p className="text-3xl font-bold">{reps} reps</p>
              <Button
                variant="default"
                className="mt-4"
                onClick={handleExerciseComplete}
                disabled={disabled}
              >
                Set Complete
              </Button>
            </div>
          )}
        </div>
      )}

      {mode === "rest" && (
        <div className="flex flex-col items-center">
          <h3 className="font-medium text-lg mb-1">Rest</h3>
          <p className="text-sm text-gray-500 mb-3">
            Next: Set {currentSet + 2} of {sets}
          </p>
          <ExerciseTimer
            duration={getRestDuration()}
            onComplete={handleRestComplete}
            isRest={true}
          />
        </div>
      )}

      {mode === "complete" && (
        <div className="text-center my-4">
          <h3 className="font-medium text-lg text-green-600 mb-1">
            All Sets Complete!
          </h3>
          <p className="text-sm text-gray-500 mb-3">Great work!</p>
          <Button variant="outline" onClick={handleRestart} disabled={disabled}>
            Start Again
          </Button>
        </div>
      )}

      {renderProgressiveLevelIndicator()}
    </div>
  );
};

export default ProgressiveTraining;
